import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Download, FileText, ImagePlus, Pencil, Save, Trash2, Upload } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ProjectInlineEditorV53 } from "../components/ProjectInlineEditorV53";
import { ProjectActivityPanelV27 } from "../components/ProjectActivityPanelV27";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APPLICATION_TYPES = ["Standard", "Wet-on-Wet", "Iris"];
const PHASES = [
  ["ready_for_submission", "Graphics Prepress"],
  ["artwork_submitted", "Artwork Submitted"],
  ["pdf_proof", "PDF Proof"],
  ["swatch", "Swatch"],
  ["pilot", "Pilot"],
  ["graphics_complete", "Graphics Complete"],
  ["in_production", "In Production"],
];

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return String(value);
};

const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const Value = ({ label, value, wide = false }) => {
  const text = displayValue(value);
  const email = looksLikeEmail(value) ? String(value).trim() : null;
  return (
    <div className={wide ? "col-span-2 md:col-span-3" : ""}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {email ? (
        <a href={`mailto:${email}`} className="font-medium break-words text-primary hover:underline">{email}</a>
      ) : (
        <p className="font-medium break-words whitespace-pre-wrap">{text}</p>
      )}
    </div>
  );
};

// inksight-v52a1b-v4-shipto
const sampleAddress = (sku, project) => {
  const structured = [
    sku?.sample_ship_to_street,
    sku?.sample_ship_to_city,
    sku?.sample_ship_to_state,
    sku?.sample_ship_to_zip,
  ].filter(Boolean).join(", ");
  if (structured) return structured;
  if (sku?.sample_ship_to_address) return sku.sample_ship_to_address;

  const projectStructured = [
    project?.sample_ship_to_street,
    project?.sample_ship_to_city,
    project?.sample_ship_to_state,
    project?.sample_ship_to_zip,
  ].filter(Boolean).join(", ");
  return projectStructured || project?.sample_ship_to_address;
};

const isCanpackProject = (project) => {
  const haystack = [
    project?.manufacturer_name,
    project?.manufacturer,
    project?.mfg,
    project?.manufacturer_id,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes("canpack") || haystack.includes("can-pack") || haystack.includes("can pack");
};

const filenameFromDisposition = (headerValue, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(headerValue || "");
  return match?.[1] || fallback;
};

const downloadFile = async (file) => {
  try {
    const response = await fetch(`${BACKEND_URL}${file.file_url}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (!response.ok) throw new Error();
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.filename || "file";
    anchor.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Failed to download file");
  }
};

// project-file-open-v50
const openProjectFileV50 = async (file) => {
  const rawUrl = String(file?.file_url || "");

  if (!rawUrl) {
    toast.error(
      "This file does not have a valid file location"
    );
    return;
  }

  // SharePoint/external files already have a browser URL.
  if (/^https?:\/\//i.test(rawUrl)) {
    window.open(
      rawUrl,
      "_blank",
      "noopener,noreferrer"
    );
    return;
  }

  // Open synchronously so the browser does not treat the
  // eventual authenticated fetch as an unsolicited popup.
  const previewWindow = window.open("", "_blank");

  if (!previewWindow) {
    toast.error(
      "Your browser blocked the file window. "
      + "Allow pop-ups for InkSight and try again."
    );
    return;
  }

  try {
    previewWindow.document.title =
      file.filename || "InkSight File";

    previewWindow.document.body.innerHTML =
      '<p style="font-family:Arial,sans-serif;padding:24px">'
      + 'Loading file...'
      + '</p>';

    const response = await fetch(
      `${BACKEND_URL}${rawUrl}`,
      {
        headers: {
          Authorization:
            `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `HTTP ${response.status}`
      );
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    previewWindow.location.replace(blobUrl);

    window.setTimeout(
      () => URL.revokeObjectURL(blobUrl),
      120000
    );
  } catch (error) {
    previewWindow.close();

    toast.error(
      "Failed to open file. "
      + "Use the download button instead."
    );
  }
};

export const ProjectDetailPageV5 = ({
  headerAddon = null,
  readOnlyHistorical = false,
  onProjectChanged = () => {},
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isManager, canChangeProjectStatus } = useAuth();
  const [project, setProject] = useState(null);
  const historicalRecord = readOnlyHistorical || project?.is_archived === true
    || ["graphics_complete", "graphics_completed", "approved"].includes(project?.workflow_phase || project?.status);
  const canEditProject = !historicalRecord && (isAdmin || isManager);
  const [customers, setCustomers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [applicationType, setApplicationType] = useState("");
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [archiveBusyV43, setArchiveBusyV43] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFilesV46, setUploadingFilesV46] = useState(false);
  const [deletingFileIdV50, setDeletingFileIdV50] = useState(null);
  const [draggingFilesV49, setDraggingFilesV49] = useState(false);
  const [exportingCanpack, setExportingCanpack] = useState(false);

  const loadActivities = async () => {
    const activityResponse = await axios.get(`${API}/projects/${id}/activity`);
    setActivities(activityResponse.data || []);
  };

  const load = async () => {
    try {
      const [projectResponse, activityResponse, customerResponse] = await Promise.all([
        axios.get(`${API}/workflow/projects/${id}`),
        axios.get(`${API}/projects/${id}/activity`),
        axios.get(`${API}/customers`),
      ]);
      setProject(projectResponse.data);
      setApplicationType(projectResponse.data.application_type || "");
      setActivities(activityResponse.data || []);
      setCustomers(customerResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const skuItems = useMemo(() => {
    if (project?.sku_items?.length) return project.sku_items;
    return project ? [project] : [];
  }, [project]);
  const firstSku = skuItems[0] || {};
  const specialtyInk = firstSku.specialty_ink_type === "Other"
    ? firstSku.specialty_ink_other
    : (firstSku.specialty_ink_type || project?.specialty_ink_type);
  const showDecorationMethod = String(project?.print_type || project?.application_type || "").toLowerCase().includes("digital") || project?.decoration_method;
  const canExportCanpack = isCanpackProject(project);

  const updatePhase = async (phase) => {
    try {
      const response = await axios.put(`${API}/workflow/projects/${id}/phase`, { phase });
      setProject(response.data);
      onProjectChanged();
      toast.success(`Moved to ${response.data.workflow_phase_label}`);
      await loadActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update phase");
    }
  };

  const saveApplicationType = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/workflow/projects/${id}/application-type`, {
        application_type: applicationType,
      });
      setProject(response.data);
      toast.success("Application Type updated");
      await loadActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update Application Type");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingImage(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await axios.post(`${API}/projects/${id}/image`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProject((previous) => ({ ...previous, project_image: response.data.image_url }));
      toast.success("Project image uploaded");
      await loadActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload project image");
    } finally {
      setUploadingImage(false);
    }
  };

  // project-add-files-v46
  const uploadProjectFilesV46 = async (source) => {
    const selectedFiles = Array.from(
      source?.dataTransfer?.files
      || source?.target?.files
      || source
      || []
    );

    if (
      source?.target
      && "value" in source.target
    ) {
      source.target.value = "";
    }

    if (!selectedFiles.length) return;

    setUploadingFilesV46(true);

    let completed = 0;

    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await axios.post(
          `${API}/projects/${id}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const rawExtension =
          file.name.includes(".")
            ? file.name.split(".").pop().toUpperCase()
            : "OTHER";

        const typeMap = {
          JPEG: "JPG",
          TIF: "TIFF",
        };

        const fileType =
          typeMap[rawExtension]
          || rawExtension
          || "OTHER";

        const bytes = Number(file.size || 0);
        const kb = bytes / 1024;
        const mb = kb / 1024;

        const displaySize =
          mb >= 1
            ? `${mb.toFixed(2)} MB`
            : kb >= 1
              ? `${kb.toFixed(2)} KB`
              : `${bytes} Bytes`;

        await axios.post(
          `${API}/projects/${id}/files`,
          {
            filename: file.name,
            file_type: fileType,
            file_size: displaySize,

            // downloadFile already prefixes
            // REACT_APP_BACKEND_URL.
            file_url: uploadResponse.data.file_url,

            notes: "",
          }
        );

        completed += 1;
      }

      toast.success(
        `${completed} file${
          completed === 1 ? "" : "s"
        } added`
      );

      await load();
    } catch (error) {
      if (completed > 0) {
        await load();
      }

      toast.error(
        error.response?.data?.detail
        || (
          completed > 0
            ? `${completed} file${
                completed === 1 ? "" : "s"
              } added before the upload stopped`
            : "Failed to add files"
        )
      );
    } finally {
      setUploadingFilesV46(false);
    }
  };

  // project-file-drag-drop-v49
  const handleProjectFileDragOverV49 = (event) => {
    if (
      historicalRecord
      || uploadingFilesV46
    ) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDraggingFilesV49(true);
  };

  const handleProjectFileDragLeaveV49 = (event) => {
    if (
      event.relatedTarget
      && event.currentTarget.contains(
        event.relatedTarget
      )
    ) {
      return;
    }

    setDraggingFilesV49(false);
  };

  const handleProjectFileDropV49 = async (event) => {
    event.preventDefault();
    setDraggingFilesV49(false);

    if (
      historicalRecord
      || uploadingFilesV46
    ) {
      return;
    }

    const files = Array.from(
      event.dataTransfer?.files || []
    );

    if (!files.length) return;

    await uploadProjectFilesV46(files);
  };

  // project-file-delete-v50
  const deleteProjectFileV50 = async (file) => {
    if (
      !file?.id
      || deletingFileIdV50
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Delete "${file.filename || "this file"}" `
      + "from this project?\n\n"
      + "This removes the file from the "
      + "InkSight project record."
    );

    if (!confirmed) return;

    setDeletingFileIdV50(file.id);

    try {
      await axios.delete(
        `${API}/projects/${id}/files/${file.id}`
      );

      setProject((previous) => ({
        ...previous,
        files: (previous?.files || []).filter(
          (item) => item.id !== file.id
        ),
      }));

      toast.success(
        `Deleted ${file.filename || "file"}`
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail
        || "Failed to delete file"
      );
    } finally {
      setDeletingFileIdV50(null);
    }
  };

  const deleteImage = async () => {
    if (!window.confirm("Remove the project image?")) return;
    try {
      await axios.delete(`${API}/projects/${id}/image`);
      setProject((previous) => ({ ...previous, project_image: null }));
      toast.success("Project image removed");
      await loadActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove project image");
    }
  };

  const downloadCanpackDesignOrder = async () => {
    setExportingCanpack(true);
    try {
      const response = await fetch(`${API}/projects/${id}/exports/canpack-design-order`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) {
        let detail = "Failed to generate CANPACK design order";
        try {
          const errorData = await response.json();
          detail = errorData?.detail || detail;
        } catch {
          // Keep the default message.
        }
        throw new Error(detail);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filenameFromDisposition(
        response.headers.get("Content-Disposition"),
        `CANPACK_Design_Order_${project?.name || "project"}.pdf`,
      );
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("CANPACK design order downloaded");
    } catch (error) {
      toast.error(error.message || "Failed to generate CANPACK design order");
    } finally {
      setExportingCanpack(false);
    }
  };


  // project-detail-recovery-v43
  const artworkContactRecord =
    project?.artwork_contact
    && typeof project.artwork_contact === "object"
      ? project.artwork_contact
      : {};

  const artworkContactName =
    project?.artwork_contact_name
    || project?.art_contact_name
    || project?.customer_artwork_contact
    || artworkContactRecord.name
    || artworkContactRecord.full_name
    || (
      typeof project?.artwork_contact === "string"
        ? project.artwork_contact
        : ""
    );

  const artworkContactEmail =
    project?.artwork_contact_email
    || project?.art_contact_email
    || artworkContactRecord.email
    || "";

  const artworkContactPhone =
    project?.artwork_contact_phone
    || project?.art_contact_phone
    || artworkContactRecord.phone
    || artworkContactRecord.phone_number
    || "";

  const artworkContactTitle =
    project?.artwork_contact_title
    || project?.art_contact_title
    || artworkContactRecord.title
    || "";

  const hasArtworkContact = Boolean(
    artworkContactName
    || artworkContactEmail
    || artworkContactPhone
    || artworkContactTitle
  );

  const archiveProjectV43 = async () => {
    if (
      !window.confirm(
        "Archive this project? It will leave active views, but all files, notes, approvals, and history will be retained."
      )
    ) {
      return;
    }

    setArchiveBusyV43(true);

    try {
      await axios.post(
        `${API}/project-archive/${id}/archive`
      );

      toast.success("Project archived");
      navigate("/project-archive");
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || "Failed to archive project"
      );
    } finally {
      setArchiveBusyV43(false);
    }
  };

  const restoreProjectV43 = async () => {
    if (
      !window.confirm(
        "Restore this project to its prior workflow stage?"
      )
    ) {
      return;
    }

    setArchiveBusyV43(true);

    try {
      await axios.post(
        `${API}/project-archive/${id}/restore`
      );

      toast.success("Project restored");
      window.location.reload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || "Failed to restore project"
      );
    } finally {
      setArchiveBusyV43(false);
    }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground">Loading project...</div>;
  if (!project) return null;

  const subtitle = project.description || project.intake_confirmation_number || "";

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Link to="/kanban" className="hover:text-foreground">Graphics Queue</Link>
            <ChevronRight size={15} />
            <span>{project.customer_name}</span>
          </div>
          <h1 className="text-3xl font-bold">{project.name}</h1>
          {headerAddon}
          {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canExportCanpack && (
            <button
              type="button"
              onClick={downloadCanpackDesignOrder}
              disabled={exportingCanpack}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary border border-border rounded-lg font-medium hover:bg-secondary/80 disabled:opacity-50"
            >
              <FileText size={17} /> {exportingCanpack ? "Generating..." : "CANPACK Design Order"}
            </button>
          )}
          {canEditProject && (
            <button
              type="button"
              onClick={() => {
                setShowEdit((value) => !value);
                window.setTimeout(() => document.getElementById("project-inline-editor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
            >
              <Pencil size={17} /> {showEdit ? "Close Editing" : "Edit Details Inline"}
            </button>
          )}


            {isAdmin && project.is_archived === true && (
              <button
                type="button"
                onClick={restoreProjectV43}
                disabled={archiveBusyV43}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                data-testid="restore-project-btn-v43"
              >
                {archiveBusyV43
                  ? "Restoring..."
                  : "Restore Project"}
              </button>
            )}

            {isAdmin
              && !historicalRecord
              && project.is_archived !== true
              && (
                <button
                  type="button"
                  onClick={archiveProjectV43}
                  disabled={archiveBusyV43}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-amber-800 bg-amber-950/30 text-amber-300 rounded-lg font-medium hover:bg-amber-950/50 disabled:opacity-50"
                  data-testid="archive-project-btn-v43"
                >
                  {archiveBusyV43
                    ? "Archiving..."
                    : "Archive Project"}
                </button>
              )}
        </div>
      </div>

      {showEdit && canEditProject && (
        <ProjectInlineEditorV53
          project={project}
          customers={customers}
          onClose={() => setShowEdit(false)}
          onSaved={(updated) => {
            setProject((previous) => ({ ...previous, ...updated }));
            setApplicationType(updated.application_type || "");
            setShowEdit(false);
            load();
            onProjectChanged();
          }}
        />
      )}

      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold">Design Order Details</h2>
            <p className="text-sm text-muted-foreground">Daily Graphics information with the complete form available on demand.</p>
          </div>
          <div className="flex items-center gap-3">
            {project.intake_group_count > 1 && (
              <span className="px-3 py-1.5 bg-secondary rounded-full text-xs font-medium">
                Group {project.intake_group_index} of {project.intake_group_count}
              </span>
            )}
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="px-4 py-2 bg-secondary border border-border rounded-lg text-sm font-medium hover:bg-secondary/80"
            >
              {expanded ? "Show Less" : "Show More"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          <Value label="Customer" value={project.customer_name || project.company_name} />
          <Value label="Manufacturer" value={project.manufacturer_name} />
          <Value label="Priority" value={project.priority} />
          <Value label="Graphics Person" value={project.graphics_person || project.assigned_graphics_person} />
          <Value label="Filler" value={project.filler || project.copacker} />
          <Value label="Brand" value={project.brand || firstSku.brand} />
          <Value label="Artwork PO" value={project.gamer_po} />
          <Value label="Customer PO" value={project.customer_po} />
          <Value label="Need By Date" value={project.target_due_date || project.deadline} />
          <Value label="Print Type" value={project.print_type} />
          {showDecorationMethod && <Value label="Decoration Method" value={project.decoration_method} />}
          <Value label="Beverage Type" value={project.beverage_type} />
          <Value label="Beer Type" value={project.beer_type} />
          <Value label="Can Size" value={project.can_size} />
          <Value label="SKU Name" value={project.sku_name || firstSku.sku_name || project.sku_flavor} />
          <Value label="Pasteurization" value={project.pasteurization_range || firstSku.pasteurization_range} />
          <Value label="Specialty Ink" value={specialtyInk} />
          <Value label="Overvarnish" value={project.overvarnish_type || firstSku.overvarnish_type} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6 pt-6 border-t border-border">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Workflow Phase</label>
            {canChangeProjectStatus && !historicalRecord ? (
              <select
                value={project.workflow_phase}
                onChange={(event) => updatePhase(event.target.value)}
                className="w-full bg-secondary border border-border rounded-lg px-3 py-2"
              >
                {!PHASES.some(([value]) => value === project.workflow_phase) && (
                  <option value={project.workflow_phase || ""}>{project.workflow_phase_label || "Intake Graphics"}</option>
                )}
                {PHASES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            ) : (
              <p className="font-medium">{project.workflow_phase_label}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Application Type</label>
            {isAdmin && !historicalRecord ? (
              <div className="flex gap-2">
                <select
                  value={applicationType}
                  onChange={(event) => setApplicationType(event.target.value)}
                  className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2"
                >
                  <option value="">Select...</option>
                  {APPLICATION_TYPES.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
                <button
                  type="button"
                  onClick={saveApplicationType}
                  disabled={saving || applicationType === (project.application_type || "")}
                  className="px-3 py-2 bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
                  aria-label="Save Application Type"
                >
                  <Save size={16} />
                </button>
              </div>
            ) : (
              <p className="font-medium">{project.application_type || "Not assigned by Graphics"}</p>
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-6 pt-6 border-t border-border space-y-8">
            <div
              data-testid="customer-contact-display-v48"
            >
              <h3 className="font-semibold mb-4">
                Customer Contact
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                <Value
                  label="Contact Name"
                  value={
                    project.customer_contact?.name
                    || project.contact_name
                  }
                />
                <Value
                  label="Contact Email"
                  value={
                    project.customer_contact?.email
                    || project.contact_email
                  }
                />
                <Value
                  label="Contact Phone"
                  value={
                    project.customer_contact?.phone
                    || project.contact_phone
                  }
                />
              </div>
            </div>


              <div
                data-testid="project-artwork-contact-v43"
              >
                <h3 className="font-semibold mb-4">
                  Artwork Contact
                </h3>

                {hasArtworkContact ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <Value
                      label="Contact Name"
                      value={artworkContactName}
                    />

                    <Value
                      label="Contact Email"
                      value={artworkContactEmail}
                    />

                    <Value
                      label="Contact Phone"
                      value={artworkContactPhone}
                    />

                    {artworkContactTitle && (
                      <Value
                        label="Title"
                        value={artworkContactTitle}
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No artwork contact is assigned to this project.
                  </p>
                )}
              </div>

            <div>
              <h3 className="font-semibold mb-4">Complete Project Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                <Value label="Sales Ops" value={project.isr} />
                <Value label="Outside Sales Rep" value={project.osr} />
                <Value label="End Size" value={project.end_size || project.neck_end || project.can_finish} />
                <Value label="End Type" value={project.end_type || project.lid_type} />
                <Value label="Application Type" value={project.application_type} />
                <Value label="Design Type" value={project.design_type || firstSku.design_type} />
                <Value label="Conversion Type" value={project.conversion_type || firstSku.conversion_type} />
                <Value label="Conversion Date" value={project.conversion_date || firstSku.conversion_date} />
                <Value label="Previous Vendor Item Number" value={project.previous_mfg_design_code || firstSku.previous_vendor_item_number} />
                <Value label="UPC" value={project.upc_code || firstSku.upc_code} />
                <Value label="Number of Colors" value={project.number_of_colors || firstSku.number_of_colors} />
                <Value label="Inside Varnish" value={project.inside_varnish_content || firstSku.inside_varnish} />
                <Value label="Symbol" value={project.symbol || firstSku.symbol} />
                <Value label="Comments / Remarks" value={project.comments || project.notes || firstSku.sku_notes} wide />
              </div>
            </div>

            {skuItems.map((sku, index) => (
              <div key={sku.id || index} className="bg-background border border-border rounded-lg p-5">
                <h3 className="font-semibold mb-4">SKU {index + 1} Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  <Value label="SKU Name" value={sku.sku_name} />
                  <Value label="Part Number" value={sku.sku_number || sku.part_number} />
                  <Value label="UPC" value={sku.upc_code} />
                  <Value label="Design Type" value={sku.design_type} />
                  <Value label="Conversion Type" value={sku.conversion_type} />
                  <Value label="Conversion Date" value={sku.conversion_date} />
                  <Value label="Previous Vendor Item Number" value={sku.previous_vendor_item_number} />
                  <Value label="Number of Colors" value={sku.number_of_colors} />
                  <Value label="Pasteurization" value={sku.pasteurization_range} />
                  <Value label="Specialty Ink" value={sku.specialty_ink_type === "Other" ? sku.specialty_ink_other : sku.specialty_ink_type} />
                  <Value label="Overvarnish" value={sku.overvarnish_type} />
                  <Value label="Inside Varnish" value={sku.inside_varnish} />
                  <Value label="Pilot Requested" value={sku.pilot_requested} />
                  <Value label="Reference / Collateral Sent" value={sku.reference_collateral_sent} />
                  <Value label="Requesting Drawdowns" value={sku.requesting_drawdowns} />
                  <Value label="Sample Recipient Name" value={sku.sample_recipient_name} />
                  <Value label="Sample Recipient Email" value={sku.sample_recipient_email} />
                  <Value label="Sample Ship-To Address" value={sampleAddress(sku, project)} wide />
                  <Value label="SKU Notes" value={sku.sku_notes} wide />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold">Project Image</h2>
            {canEditProject && project.project_image && (
              <button type="button" onClick={deleteImage} className="p-2 rounded-lg text-red-400 hover:bg-red-950/30" aria-label="Remove project image">
                <Trash2 size={17} />
              </button>
            )}
          </div>
          {project.project_image ? (
            <img
              src={`${BACKEND_URL}${project.project_image}`}
              alt={project.name}
              className="w-full max-h-72 object-contain rounded-lg border border-border bg-background"
            />
          ) : (
            <div className="border border-dashed border-border rounded-lg py-12 text-center text-sm text-muted-foreground">
              No project image uploaded.
            </div>
          )}
          {canEditProject && (
            <label className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-secondary border border-border rounded-lg font-medium cursor-pointer hover:bg-secondary/80">
              <ImagePlus size={17} /> {uploadingImage ? "Uploading..." : project.project_image ? "Replace Image" : "Upload Image"}
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" disabled={uploadingImage} onChange={uploadImage} />
            </label>
          )}
          <p className="text-xs text-muted-foreground mt-2">JPG, PNG, GIF, or WebP. Maximum 5 MB.</p>
        </section>

        <section className="bg-card border border-border rounded-xl p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">
              Files
            </h2>

            {!historicalRecord && (
              <label
                className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                data-testid="add-project-files-v46"
              >
                <Upload size={16} />

                {uploadingFilesV46
                  ? "Uploading..."
                  : "Add Files"}

                <input
                  type="file"
                  multiple
                  className="hidden"
                  disabled={uploadingFilesV46}
                  onChange={uploadProjectFilesV46}
                />
              </label>
            )}
          </div>
          {!historicalRecord && (
            <div
              data-testid="project-file-dropzone-v49"
              onDragEnter={handleProjectFileDragOverV49}
              onDragOver={handleProjectFileDragOverV49}
              onDragLeave={handleProjectFileDragLeaveV49}
              onDrop={handleProjectFileDropV49}
              className={`mb-4 flex min-h-24 flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
                draggingFilesV49
                  ? "border-primary bg-primary/10"
                  : "border-border bg-secondary/20"
              }`}
            >
              <Upload size={24} className="mb-2" />

              <p className="text-sm font-medium">
                {uploadingFilesV46
                  ? "Uploading files..."
                  : draggingFilesV49
                    ? "Drop files to upload"
                    : "Drag and drop files here"}
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Or use Add Files above. Multiple files are supported.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {(project.files || []).map((file) => (
              <div
                key={file.id}
                className="w-full flex items-center gap-2 bg-secondary rounded-lg px-3 py-2"
                data-testid="project-file-row-v50"
              >
                <button
                  type="button"
                  onClick={() =>
                    openProjectFileV50(file)
                  }
                  className="min-w-0 flex-1 text-left"
                  title={`Open ${
                    file.filename || "file"
                  }`}
                  data-testid="project-file-open-v50"
                >
                  <span className="block truncate font-medium text-primary hover:underline">
                    {file.filename}
                  </span>

                  {(file.file_size
                    || file.file_type) && (
                    <span className="block truncate text-xs text-muted-foreground mt-0.5">
                      {[
                        file.file_type,
                        file.file_size,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    downloadFile(file)
                  }
                  className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-background hover:text-foreground"
                  title={`Download ${
                    file.filename || "file"
                  }`}
                  aria-label={`Download ${
                    file.filename || "file"
                  }`}
                  data-testid="project-file-download-v50"
                >
                  <Download size={16} />
                </button>

                {isAdmin
                  && !historicalRecord
                  && (
                    <button
                      type="button"
                      onClick={() =>
                        deleteProjectFileV50(file)
                      }
                      disabled={
                        deletingFileIdV50
                        === file.id
                      }
                      className="shrink-0 rounded-md p-2 text-red-400 hover:bg-red-950/30 disabled:opacity-50"
                      title={`Delete ${
                        file.filename || "file"
                      }`}
                      aria-label={`Delete ${
                        file.filename || "file"
                      }`}
                      data-testid="project-file-delete-v50"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
              </div>
            ))}
            {(project.files || []).length === 0 && <p className="text-sm text-muted-foreground">No project files.</p>}
          </div>
        </section>

        <ProjectActivityPanelV27
          projectId={id}
          activities={activities}
          setActivities={setActivities}
          readOnlyHistorical={historicalRecord}
        />
      </div>


    </div>
  );
};
